import { Button, Input, message, Modal, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { getAuditLogs } from 'services';
import { convertDateTime } from 'utils';

const { Search } = Input;
const { Option } = Select;

type AuditLog = {
  _id: string;
  adminEmail: string;
  action: string;
  targetEmails: string[];
  targetUserIDs: string[];
  evidence: Record<string, any>;
  createdAt: string;
};

interface AuditLogsProps {
  onClose?: () => void;
}

const AuditLogs = ({ onClose }: AuditLogsProps) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);

  const pageSize = 50;

  useEffect(() => {
    fetchAuditLogs(currentPage, searchKeyword, actionFilter);
  }, [currentPage, searchKeyword, actionFilter]);

  const fetchAuditLogs = async (page: number, search: string, action: string | null) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = { limit: pageSize, offset };
      if (search.trim()) {
        params.search = search.trim();
      }
      if (action) {
        params.action = action;
      }
      const response = await getAuditLogs(params);
      setData(response.data.logs.map((log: any) => ({ ...log, key: log._id })));
      setTotal(response.data.total);
    } catch (error: any) {
      message.error('Failed to load audit logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    setCurrentPage(1);
  };

  const handleActionFilterChange = (value: string) => {
    setActionFilter(value);
    setCurrentPage(1);
  };

  const getActionTag = (action: string) => {
    let color = 'blue';
    switch (action) {
      case 'block':
        color = 'red';
        break;
      case 'unblock':
        color = 'green';
        break;
      case 'delete':
        color = 'volcano';
        break;
      default:
        color = 'blue';
    }
    return <Tag color={color}>{action.toUpperCase()}</Tag>;
  };

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Admin',
      dataIndex: 'adminEmail',
      key: 'adminEmail',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action: string) => getActionTag(action),
    },
    {
      title: 'Target Emails',
      dataIndex: 'targetEmails',
      key: 'targetEmails',
      width: 250,
      render: (emails: string[]) => {
        if (!emails || emails.length === 0) return 'N/A';
        const displayEmails = emails.slice(0, 3);
        const remainingCount = emails.length - displayEmails.length;
        return (
          <div
            onClick={() => {
              setSelectedEmails(emails);
              setShowEmailModal(true);
            }}
            style={{ cursor: 'pointer' }}
          >
            <Space direction="vertical" size={0}>
              {displayEmails.map((email, index) => (
                <Typography.Text key={index} ellipsis={true} style={{ maxWidth: '200px' }}>{email}</Typography.Text>
              ))}
              {remainingCount > 0 && <Typography.Text type="secondary">+{remainingCount} more</Typography.Text>}
              <Typography.Link style={{ fontSize: '12px' }}>
                View all ({emails.length})
              </Typography.Link>
            </Space>
          </div>
        );
      },
    },
    {
      title: 'Count',
      dataIndex: 'evidence',
      key: 'count',
      width: 80,
      render: (evidence: Record<string, any>) => {
        if (evidence.deletedCount !== undefined) return evidence.deletedCount;
        if (evidence.modifiedCount !== undefined) return evidence.modifiedCount;
        return 'N/A';
      },
    },
    {
      title: 'Evidence',
      dataIndex: 'evidence',
      key: 'evidence',
      render: (evidence: Record<string, any>) => (
        <Tooltip title={JSON.stringify(evidence, null, 2)}>
          <Typography.Text ellipsis={true} style={{ maxWidth: '300px' }}>
            {JSON.stringify(evidence)}
          </Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value: string) => convertDateTime(value),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space>
        <Search
          placeholder="Search by admin or target email"
          allowClear
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onSearch={handleSearch}
          style={{ width: 300 }}
          enterButton
          loading={loading}
        />
        <Select
          placeholder="Filter by Action"
          allowClear
          value={actionFilter || undefined}
          onChange={handleActionFilterChange}
          style={{ width: 180 }}
        >
          <Option value="block">Block</Option>
          <Option value="unblock">Unblock</Option>
          <Option value="delete">Delete</Option>
        </Select>
      </Space>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          onChange: (page) => setCurrentPage(page),
          showSizeChanger: false,
        }}
        rowKey="_id"
        scroll={{ y: 600 }}
      />
      <Modal
        title="Target Emails"
        open={showEmailModal}
        onCancel={() => setShowEmailModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowEmailModal(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%', maxHeight: '400px', overflowY: 'auto' }}>
          {selectedEmails.map((email, index) => (
            <Typography.Text key={index} style={{ display: 'block', padding: '4px 0' }}>
              {email}
            </Typography.Text>
          ))}
        </Space>
      </Modal>
    </Space>
  );
};

export default AuditLogs;

